/**
 * BannerAd Component
 * Reusable banner advertisement component with responsive design and tracking
 */

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useAdTracking } from "@/hooks/useAdTracking";
import type { Advertisement, AdFormat } from "@/types/advertising";

interface BannerAdProps {
  ad: Advertisement;
  className?: string;
  priority?: boolean;
}

/**
 * BannerAd component displays horizontal banner advertisements
 * with automatic impression tracking and click analytics
 */
const BannerAd: React.FC<BannerAdProps> = ({
  ad,
  className = "",
  priority = false,
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { adRef, handleClick, impressionTracked } = useAdTracking({
    adId: ad.id,
    placement: ad.placement,
    targetUrl: ad.targetUrl,
  });

  const handleImageError = () => {
    console.error(`Failed to load banner ad image: ${ad.imageUrl}`);
    setImageError(true);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleAdClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    handleClick(event);
  };

  // Don't render if ad is not active
  if (!ad.isActive) {
    return null;
  }

  // Handle different ad formats
  const renderAdContent = () => {
    switch (ad.format) {
      case "image":
        return renderImageAd();
      case "text":
        return renderTextAd();
      case "video":
        return renderVideoAd();
      default:
        return renderImageAd();
    }
  };

  const renderImageAd = () => {
    if (!ad.imageUrl || imageError) {
      return renderTextAd();
    }

    return (
      <div className="relative w-full aspect-[728/90] md:aspect-[970/250]">
        {isLoading && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-lg" />
        )}
        <Image
          src={ad.imageUrl}
          alt={ad.altText || ad.title}
          fill
          className="object-cover rounded-lg"
          onError={handleImageError}
          onLoad={handleImageLoad}
          priority={priority}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1200px"
        />
      </div>
    );
  };

  const renderTextAd = () => {
    return (
      <div className="flex flex-col justify-center items-center p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg h-full">
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 text-center">
          {ad.title}
        </h3>
        {ad.description && (
          <p className="text-sm md:text-base text-gray-700 text-center max-w-2xl">
            {ad.description}
          </p>
        )}
        <div className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 transition-colors">
          Learn More
        </div>
      </div>
    );
  };

  const renderVideoAd = () => {
    if (!ad.videoUrl) {
      return renderTextAd();
    }

    return (
      <div className="relative w-full aspect-[728/90] md:aspect-[970/250]">
        <video
          src={ad.videoUrl}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover rounded-lg"
          onError={() => {
            console.error(`Failed to load banner ad video: ${ad.videoUrl}`);
            setImageError(true);
          }}
        >
          Your browser does not support the video tag.
        </video>
      </div>
    );
  };

  return (
    <div
      ref={adRef}
      className={`banner-ad relative w-full ${className}`}
      data-ad-id={ad.id}
      data-tracked={impressionTracked}
      role="complementary"
      aria-label="Advertisement"
    >
      {/* Advertisement label for transparency */}
      <div className="absolute top-2 right-2 z-10">
        <span className="text-xs bg-gray-800 bg-opacity-75 text-white px-2 py-1 rounded">
          Ad
        </span>
      </div>

      <Link
        href={ad.targetUrl}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={handleAdClick}
        className="block w-full h-full transition-transform hover:scale-[1.02] duration-200"
        aria-label={`Advertisement: ${ad.title}`}
      >
        {renderAdContent()}
      </Link>
    </div>
  );
};

export default BannerAd;
